// Builds the system prompt that frames the LLM. The goal is to give it
// enough live dashboard context to answer specific questions without
// shipping the entire dataset (which would blow the context window).
//
// Strategy:
//   1. A persistent "platform identity" section — what the dashboard
//      is, the citation rule, the response format.
//   2. A live snapshot block — current values of the headline KPIs,
//      the customer top-10 by AR, recent distress events, commodity
//      flags.
//   3. The KPI catalog summary — small enough to fit, big enough that
//      the LLM can answer "what is K-014?" without hallucinating.

import type { Dataset } from "../types";
import { KPI_CATALOG } from "../kpiCatalog";

const PLATFORM_IDENTITY = `You are the analyst inside Cashflow Sentinel — an
enterprise cash-intelligence dashboard for a US specialty contractor. You
answer questions about the live snapshot the user is looking at.

CONSTRAINTS — these are non-negotiable.
1. Cite the K-ID for every claim. No claim without a citation.
2. Use the provided live values. Do not invent numbers. If a number is
   not in the snapshot below, say so.
3. Distinguish what the data SHOWS from what it suggests. State the
   evidence first, the implication second.
4. Keep answers tight: 2-5 short paragraphs or a short bullet list.
   No filler adjectives ("concerning", "significant"). State magnitudes.
5. If the user asks about a tab or KPI not in this dashboard, name the
   closest in-dashboard equivalent.
6. When the user asks "what should we do?", point to the dashboard
   surface that answers it (e.g., Simulator for scenario testing,
   Customer 360 for per-customer drill-down) — do not invent policy.

PLATFORM
- Five intelligence layers: Portfolio Billing (PB), AR & Customer Risk
  (AR), Net Income Forecast (NIF), Simulator (SIM), Decision Pack (DP).
- User-facing tabs: Overview · Finance & Risk (Portfolio Billing · Net
  Income Forecast · AR & Customer Risk · Simulator) · Advanced Analytics
  (Customer 360 · Industry) · Tools (Zen Insights · Chat) · Resources
  (KPI Reference · FAQ · About Oraczen).
- Source: D365 F&O via Synapse Link → analytics tables. Vintage label is
  shown in the topbar.
- Forecasts use conformal prediction intervals (50/80/95). Customer risk
  K-028 is a deterministic additive composite over 11 features.`;

function fmtMoney(n: number | undefined | null): string {
  if (n == null || !Number.isFinite(n)) return "—";
  const abs = Math.abs(n);
  if (abs >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (abs >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (abs >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function snapshotBlock(data: Dataset): string {
  const meta = data.meta;
  const ek = data.kpi_enterprise as unknown as Record<string, number | undefined>;

  // Customer name + tier lookup keyed on account_num.
  const custByAccount = new Map(data.customers.map((c) => [c.customer_account, c]));

  const top10Cust = [...data.ar_by_customer]
    .sort((a, b) => (b.ar_balance ?? 0) - (a.ar_balance ?? 0))
    .slice(0, 10);
  const totalAr = data.ar_by_customer.reduce((s, c) => s + (c.ar_balance ?? 0), 0);
  const top10Share = totalAr ? (top10Cust.reduce((s, c) => s + (c.ar_balance ?? 0), 0) / totalAr) * 100 : 0;

  const overUnder = data.billing_summary.reduce((s, b) => s + (b.over_under_billed ?? 0), 0);
  const activeProjects = data.billing_summary.filter((b) => (b.last_pct_complete ?? 0) < 1).length;

  const commodityFlags = (data.industry_commodities?.items ?? []).filter((c) => c.flag !== "OK");
  const recentEvents = data.credit_events.slice(0, 8);

  const macroLines = (data.industry_macro?.items ?? []).map((m) => `  ${m.code} ${m.name}: ${m.latest} (${m.monthChangeLabel})`);
  const sentimentLast = data.industry_news?.items?.[0];

  const lines: string[] = [];
  lines.push(`SNAPSHOT (asOf ${meta.asOfDisplay})`);
  lines.push("");
  lines.push("Headline values:");
  lines.push(`  K-001 Aggregate over/underbilling: ${fmtMoney(overUnder)} across ${activeProjects} active projects`);
  if (ek.K023_backlog != null) lines.push(`  K-023 Backlog (uncompleted contract value): ${fmtMoney(ek.K023_backlog)}`);
  if (ek.active_projects != null) lines.push(`  Active projects: ${ek.active_projects} · Closed: ${ek.closed_projects ?? "—"}`);
  lines.push(`  K-018 Top-10 customer AR concentration: ${top10Share.toFixed(1)}% of total AR (${fmtMoney(totalAr)})`);
  lines.push("");

  lines.push("Top-10 customers by AR balance:");
  for (const c of top10Cust) {
    const cust = custByAccount.get(c.account_num);
    const tier = cust?.tier_display ?? cust?.tier ?? "?";
    const name = cust?.customer_name ?? c.account_num;
    const payCycle = c.last_pay_cycle_days != null ? `${Math.round(c.last_pay_cycle_days)}d pay-cycle` : "no recent pay event";
    lines.push(`  ${c.account_num} · ${name} · tier ${tier} · AR ${fmtMoney(c.ar_balance)} · ${payCycle}`);
  }
  lines.push("");

  if (data.dso_by_tier && Object.keys(data.dso_by_tier).length > 0) {
    lines.push("K-013 DSO by tier (days):");
    for (const [tier, dso] of Object.entries(data.dso_by_tier)) {
      lines.push(`  ${tier}: ${Number(dso).toFixed(1)}d`);
    }
    lines.push("");
  }

  if (data.ar_aging && Object.keys(data.ar_aging).length > 0) {
    lines.push("K-016 AR aging:");
    for (const [bucket, amount] of Object.entries(data.ar_aging)) {
      lines.push(`  ${bucket}: ${fmtMoney(Number(amount))}`);
    }
    lines.push("");
  }

  if (commodityFlags.length > 0) {
    lines.push("Commodity flags (non-OK):");
    for (const c of commodityFlags) {
      lines.push(`  ${c.code} ${c.name}: ${c.flag} · z=${c.z.toFixed(1)}σ · ${c.monthDeltaPct >= 0 ? "+" : ""}${c.monthDeltaPct.toFixed(1)}% m/m · exposes ${c.exposureArchetype}`);
    }
    lines.push("");
  }

  if (macroLines.length > 0) {
    lines.push("Macro indicators (Industry tab):");
    lines.push(...macroLines);
    lines.push("");
  }

  if (sentimentLast) {
    lines.push(`Most recent sector news (${sentimentLast.date}, sentiment ${sentimentLast.sentiment >= 0 ? "+" : ""}${sentimentLast.sentiment.toFixed(2)}):`);
    lines.push(`  ${sentimentLast.headline}`);
    lines.push("");
  }

  if (recentEvents.length > 0) {
    lines.push(`Recent credit / distress events (last ${recentEvents.length}):`);
    for (const e of recentEvents) {
      lines.push(`  ${e.event_date} · ${e.customer_account} · ${e.event_type} · ${e.severity}`);
    }
    lines.push("");
  }

  return lines.join("\n");
}

function kpiCatalogBlock(): string {
  const lines = ["KPI CATALOG (one-liner per indicator):"];
  for (const k of KPI_CATALOG) {
    lines.push(`  ${k.id} [${k.type} · ${k.impact}] ${k.name} — ${k.summary}`);
  }
  return lines.join("\n");
}

export function buildSystemPrompt(data: Dataset): string {
  return [
    PLATFORM_IDENTITY,
    "",
    snapshotBlock(data),
    "",
    kpiCatalogBlock(),
  ].join("\n");
}
